import { DTMF_KEYS, STATUS_PRIORITY } from "./constant";
import { Notify } from "./notification";
import { Fetcher } from "./fetcher";

// A reseller/end-user has no ACL of their own -- whether they should see a
// given module (TTS Plans, API Docs, ...) depends on whichever sub-superadmin's
// ACL they were created under. Resolves that effective ACL and checks the
// module's view_permission; resolves true when there's no restricting ancestor
// (e.g. created directly by superadmin) or on any fetch error (fail open, same
// as before this check existed).
export const isModuleAllowedForNonSubSuperAdmin = async (apiPath, userId, moduleName) => {
    try {
        const res = await Fetcher.get(`${apiPath}/api/obd/user/effective/acl/${userId}`);
        if (!res?.aclId) return true;
        const aclRes = await Fetcher.get(`${apiPath}/api/obd/get/acl/setting/${res.aclId}`);
        const permission = aclRes?.moduleOperation
            ? (typeof aclRes.moduleOperation === "string" ? JSON.parse(aclRes.moduleOperation) : aclRes.moduleOperation)
            : {};
        return !!permission?.[moduleName]?.view_permission;
    } catch (error) {
        console.log("isModuleAllowedForNonSubSuperAdmin error", error);
        return true;
    }
};

// Copies the creating user's UI prefs (brand color, sidebar position, ...)
// onto a newly created user, so they cascade down through the hierarchy at
// creation time (there's no reliable parent-id available from the backend
// to resolve them dynamically).
export const copyUiSettingsToNewUser = async (apiPath, creatorUserId, newUserId) => {
    if (!apiPath || !creatorUserId || !newUserId) return;
    try {
        const uiRes = await Fetcher.get(`${apiPath}/api/obd/user/${creatorUserId}/ui/settings`);
        const parsed = uiRes?.json ? JSON.parse(uiRes.json) : {};
        if (parsed && Object.keys(parsed).length > 0) {
            await Fetcher.post(`${apiPath}/api/obd/user/${newUserId}/ui/settings`, {
                method: "POST",
                headers: { "Content-type": "application/json" },
                body: JSON.stringify({ json: JSON.stringify(parsed) })
            });
        }
    } catch (err) {
        console.log("copyUiSettingsToNewUser error", err);
    }
};

export const statusSortFunc = (a, b, order, dataField, rowA, rowB) => {
    const getPriority = (status) => {
        return STATUS_PRIORITY[status] !== undefined ? STATUS_PRIORITY[status] : 100;
    };

    const priorityA = getPriority(rowA.campaignstatus);
    const priorityB = getPriority(rowB.campaignstatus);

    if (priorityA !== priorityB) {
        if (order === 'asc') {
            return priorityA - priorityB;
        } else {
            return priorityB - priorityA;
        }
    }

    const countA = rowA.numbersUploaded;
    const countB = rowB.numbersUploaded;

    return countB - countA;
};

export const convertToAMPM = (timeString) => {
    if (!timeString) return '';

    const timePart = timeString.includes(' ') ? timeString.split(' ')[1] : timeString;

    let [hours, minutes] = timePart.split(':');

    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);

    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    hours = hours ? hours : 12;

    minutes = minutes < 10 ? `0${minutes}` : minutes;

    return `${hours}:${minutes} ${ampm}`;
};

export const parseCurl = (curl) => {
    try {
        if (!curl || typeof curl !== "string") {
            return { url: "", method: "GET", headers: {}, body: "" };
        }

        // --- CLEANUP INVISIBLE CHARACTERS ---
        curl = curl
            .replace(/\u00A0/g, " ")   // Non-breaking space
            .replace(/\u200B/g, "")    // Zero-width space
            .replace(/\s{2,}/g, " ")   // Compress multiple spaces
            .replace(/\\\r?\n/g, " ")  // Join multiline
            .trim();

        // Extract URL
        let urlMatch = curl.match(/(https?:\/\/[^\s'"]+)/i);
        let url = urlMatch ? urlMatch[1] : "";
        // --- REMOVE TRAILING QUOTES OR %27 SAFELY ---
        if (url) {
            // Only remove if at the very end
            url = url.trim();
            url = url.replace(/'+$/, '');
            // Decode URL to make it human-readable
            try {
                url = decodeURIComponent(url);
            } catch (e) {
                // If decoding fails, leave as-is
            }
        }
        // Extract method from -X
        let method = "GET";
        const methodMatch = curl.match(/-X\s+(\w+)/i);
        if (methodMatch) method = methodMatch[1].toUpperCase();

        // Force GET if -G flag exists
        if (/\s-G\s/.test(curl)) method = "GET";

        // Default to POST if data exists and no explicit method
        const hasDataFlag = /\s(--data|-d|--data-raw|--data-binary|--form|-F|--data-urlencode)\s+/.test(curl);
        if (!methodMatch && !/\s-G\s/.test(curl) && hasDataFlag) method = "POST";

        // Extract headers (-H, --header)
        const headers = {};
        const headerMatches = [...curl.matchAll(/(?:-H|--header)\s+['"]?([^'"\n]+)['"]?/gi)];
        headerMatches.forEach((m) => {
            const headerLine = m[1].trim();
            const [key, ...rest] = headerLine.split(":");
            if (key && rest.length) headers[key.trim()] = rest.join(":").trim();
        });

        // Extract body (supports multiple flags)
        const dataFlags = ["-d", "--data", "--data-raw", "--data-binary", "--form", "-F"];
        let body = "";

        for (const flag of dataFlags) {
            // Quoted body
            const matchQuoted = curl.match(new RegExp(`${flag}\\s+(['"])([\\s\\S]*?)\\1`));
            if (matchQuoted) {
                body = matchQuoted[2].trim();
                break;
            }
            // Unquoted body
            const matchUnquoted = curl.match(new RegExp(`${flag}\\s+([^\\s]+)`));
            if (matchUnquoted) {
                body = matchUnquoted[1].trim();
                break;
            }
        }

        // Handle --data-urlencode (multiple occurrences)
        const params = [];
        const urlEncodeRegex = /--data-urlencode\s+(?:'([^']+)'|"([^"]+)"|([^\s]+))/gi;
        let encoded;
        while ((encoded = urlEncodeRegex.exec(curl)) !== null) {
            const param = encoded[1] || encoded[2] || encoded[3]; // pick whichever matched
            params.push(param.trim());
        }

        // Append encoded params to URL if GET
        if (method === "GET" && params.length) {
            const query = params.join("&");
            url += (url.includes("?") ? "&" : "?") + query;
        }

        // Parse the body as JSON to preserve data types
        let parsedBody = body;
        if (body && method !== "GET") {
            try {
                // Try to parse as JSON to preserve data types
                parsedBody = JSON.parse(body);
                // Convert back to string with proper formatting
                body = JSON.stringify(parsedBody);
            } catch (e) {
                // If not valid JSON, keep as is
            }
        }

        return { url, method, headers, body };
    } catch (error) {
        console.error("Error parsing curl:", error);
        return { url: "", method: "GET", headers: {}, body: "" };
    }
};

export const convertArrayToString = (headersArray) => {
    const hasValidHeaders = headersArray.some(param =>
        param.key && param.key.trim() !== ''
    );

    if (!hasValidHeaders) {
        return '';
    }

    const headersObj = headersArray.reduce((acc, { key, value }) => {
        if (key && key.trim() !== '') {
            acc[key] = value || '';
        }
        return acc;
    }, {});
    const cleanJson = parseNestedStrings(headersObj)
    return JSON.stringify(cleanJson, null, 2);
};

export const convertStringToArray = (headersString) => {
    if (!headersString || headersString.trim() === '') {
        return [{ key: "", value: "" }];
    }

    try {
        const headersObj = JSON.parse(headersString);
        return Object.keys(headersObj).map(key => ({
            key,
            value: (headersObj[key] && typeof headersObj[key] === 'object' && !Array.isArray(headersObj[key]))
                ? JSON.stringify(headersObj[key])
                : headersObj[key]
        }));
    } catch (e) {
        console.error('Error parsing headers string:', e);
        return [{ key: "", value: "" }];
    }
}
export const syntaxHighlight = (json) => {
    if (typeof json !== "string") {
        json = JSON.stringify(json, null, 2);
    }
    json = json
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(
            /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|\b-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?\b)/g,
            (match) => {
                let cls = "number";
                if (/^"/.test(match)) {
                    if (/:$/.test(match)) {
                        cls = "key";
                    } else {
                        cls = "string";
                    }
                } else if (/true|false/.test(match)) {
                    cls = "boolean";
                } else if (/null/.test(match)) {
                    cls = "null";
                }
                return `<span class="${cls}">${match}</span>`;
            }
        );
    return json;
};

export const ensureArray = (data) => {
    return Array.isArray(data) ? data : []
}

export function isExpired(expiryDate) {
    if (!expiryDate) return false;
    const exp = new Date(expiryDate.replace(" ", "T"));
    return exp < new Date();
}

// Replicates react-bootstrap-table2-toolkit's internal SearchProvider.search()
// algorithm so we can compute the full search-matched set (all pages) ourselves --
// ToolkitProvider's baseProps.data is always the raw, unfiltered data prop, and
// onSelectAll's `rows` argument is only the current page, so neither can be used
// directly to select "every row matching the current search".
export const filterTableDataBySearch = (data, columns, searchText) => {
    if (!searchText) return data;
    const term = searchText.toString().toLowerCase();
    return data.filter((row) => {
        for (let i = 0; i < columns.length; i++) {
            const column = columns[i];
            if (column.searchable === false) continue;
            let targetValue = row[column.dataField];
            if (targetValue !== null && typeof targetValue !== 'undefined') {
                targetValue = targetValue.toString().toLowerCase();
                if (targetValue.indexOf(term) > -1) {
                    return true;
                }
            }
        }
        return false;
    });
};

export function parseNestedStrings(obj) {
    if (typeof obj === "string") {
        const trimmed = obj.trim();
        if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) {
            return obj;
        }
        try {
            const parsed = JSON.parse(trimmed);
            return parseNestedStrings(parsed);
        } catch {
            return obj;
        }
    }

    if (Array.isArray(obj)) {
        return obj.map(parseNestedStrings);
    }

    if (obj && typeof obj === "object") {
        return Object.fromEntries(
            Object.entries(obj).map(([k, v]) => [k, parseNestedStrings(v)])
        );
    }

    return obj;
}

const getDtmfIndex = (edge) => {
    const key = edge.sourceHandle?.split('-').pop();
    const idx = DTMF_KEYS.indexOf(key);
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

// Recursive function to calculate left-most path duration
const calculateLeftPathDuration = (node, nodesArray, edges, visited = new Set()) => {
    if (!node) return 0;

    // Always add the node's own duration first
    let duration = 0;
    if (node.data?.verb === "play") {
        duration += node.data?.action?.duration || 0;
    } else if (node.data?.verb === "record") {
        duration += node.data?.action?.duration || 0;
    } else if (node.data?.verb === "dtmf" || node.data?.verb === "gather") {
        duration += Number(node.data?.timeout ?? 0);
        duration += node.data?.action?.menu?.duration ?? 0;
    }

    // Avoid infinite loops by marking visited AFTER counting current node
    if (visited.has(node.id)) return duration;
    visited.add(node.id);

    const edgeData = edges.filter(e => e.source === node.id);

    if (node.data?.verb === "dtmf") {
        const sortedEdgeData = [...edgeData].sort((a, b) => getDtmfIndex(a) - getDtmfIndex(b));
        if (sortedEdgeData.length > 0) {
            const firstEdge = sortedEdgeData[0];
            const targetNode = nodesArray.find(n => n.id === firstEdge.target);
            duration += calculateLeftPathDuration(targetNode, nodesArray, edges, visited);
        }
    } else if (edgeData.length > 0) {
        const firstEdge = edgeData[0];
        const targetNode = nodesArray.find(n => n.id === firstEdge.target);
        duration += calculateLeftPathDuration(targetNode, nodesArray, edges, visited);
    }

    return duration;
};

export const transformNodes = ({
    nodesArray,
    edges,
    flowName,
    scheduleData,
    isGatherNode,
    userId,
    smsWebhookFlag
}) => {
    const cid = userId;
    const nodeIdMap = new Map(nodesArray.map((node, index) => [node.id, index]));

    // Start from the first node in nodesArray (assume it's root)
    const totalDuration = calculateLeftPathDuration(nodesArray[0], nodesArray, edges);

    const dtmfTargetNodeIds = new Set();

    const transformedNodes = nodesArray.reduce((acc, node, index) => {
        const edgeData = edges.filter(obj => obj.source === node.id);
        const nodeId = nodeIdMap.get(node.id);

        const flowJson = { verb: node.data?.verb };

        if (node.data?.verb === "play") {
            flowJson.action = node.data?.action;
            flowJson.duration = node.data?.action?.duration || 0;
        }
        if (node.data?.verb === "record") {
            flowJson.action = node.data?.action;
            flowJson.duration = node.data?.action?.duration || 0;
            flowJson.record_duration = node.data?.record_duration
        }
        else if (node.data?.verb === "dtmf" || node.data?.verb === "gather") {
            flowJson.type = node.data?.type;
            flowJson.action = node.data?.action;
            flowJson.numDigits = node.data?.numDigits;
            flowJson.timeout = node.data?.timeout;
            flowJson.numSources = node.data?.numSources;
            flowJson.rePrompt = node.data?.rePrompt;
            flowJson.ttsRows = node.data?.ttsRows ?? [];
            flowJson.minValue = node.data?.minValue ?? "";
            flowJson.manualDigits = node.data?.manualDigits ?? ""
            flowJson.smsData = node.data?.smsData ?? {};
        }
        else if (node.data?.verb === "transfer") {
            flowJson.action = node.data?.action;
        }

        let flowControl = {};

        if (node.data?.verb === "dtmf" && Object.keys(node.data?.selectedHandles ?? {}).length !== 0) {
            const sortedEdgeData = [...edgeData].sort((a, b) => getDtmfIndex(a) - getDtmfIndex(b));
            sortedEdgeData.forEach(edge => {
                const key = edge.sourceHandle?.split('-').pop();
                const targetNumericId = nodeIdMap.get(edge.target);
                const targetNode = nodesArray.find(n => n.id === edge.target);

                if (targetNode.data?.verb === "play" || targetNode.data?.verb === "record") dtmfTargetNodeIds.add(targetNode.id);
                if (!key || targetNumericId === undefined) return;

                flowControl[`dtmf_${key}`] = {
                    flowName,
                    nextNodeId: targetNumericId,
                    isLoop: edge.source === edge.target,
                    parent: node.data?.parent ?? ""
                };
            });

        }
        else if (edgeData.length > 0) {
            const targetNumericId = nodeIdMap.get(edgeData[0].target);
            if (targetNumericId) {
                flowControl.default = {
                    flowName,
                    nextNodeId: targetNumericId,
                    parent: node.data?.parent ?? ""
                };
            }
        }

        acc[nodeId] = {
            id: nodeId,
            flowJson,
            flowRender: { ...node },
            flowControl
        };

        return acc;
    }, {});

    return {
        flowName,
        userId: cid,
        nodes: transformedNodes,
        scheduleData,
        isGatherNode,
        promptDuration: totalDuration,
        smsWebhookFlag
    };
};

export const validateIVR = ({ nodes = [], edges = [], flowName }) => {
    if (!nodes.length) {
        Notify.notify("Add at least one node");
        return false;
    }

    if (!flowName) {
        Notify.notify("IVR Name required");
        return false;
    }

    // 🔹 Build edge maps for O(1) lookups
    const incomingMap = new Map();
    const outgoingMap = new Map();

    for (const edge of edges) {
        if (!incomingMap.has(edge.target)) {
            incomingMap.set(edge.target, []);
        }
        if (!outgoingMap.has(edge.source)) {
            outgoingMap.set(edge.source, []);
        }

        incomingMap.get(edge.target).push(edge);
        outgoingMap.get(edge.source).push(edge);
    }

    for (const node of nodes) {
        const incoming = incomingMap.get(node.id) || [];
        const outgoing = outgoingMap.get(node.id) || [];
        const label = String(node?.data?.label ?? "").toUpperCase();

        /* Rule 1: Input handle must have at least one connection (except answer nodes) */
        if (
            node.data?.hasInput &&
            node.type !== "answerNode" &&
            incoming.length === 0
        ) {
            Notify.notify(`Node "${label}" must have at least one input connected`);
            return false;
        }

        /* Rule 2: Selected output handles must be connected */
        const selectedHandles = node.data?.selectedHandles;
        if (selectedHandles) {
            for (const [handleId, handleObj] of Object.entries(selectedHandles)) {
                if (!handleObj || Object.keys(handleObj).length === 0) continue;

                const isConnected = outgoing.some(
                    edge => edge.sourceHandle === handleId
                );

                if (!isConnected) {
                    const dtmf = handleId.split("-")[1];
                    Notify.notify(
                        `Output handle "DTMF_${dtmf}" of node "${label}" must be connected`
                    );
                    return false;
                }
            }
        }

        /* Rule 3: End nodes must be Hangup nodes */
        if (outgoing.length === 0 && node.type !== "hangupNode") {
            Notify.notify(`End node "${label}" must be a Hangup node`);
            return false;
        }

        /* Rule 4: Play node must have audio */
        if (node.type === "playNode" && !node?.data?.action?.audio) {
            Notify.notify(`Node "${label}" must select an audio file`);
            return false;
        }

        /* Rule 5: Record node must have audio ad record_duration*/
        if (node.type === "recordNode") {
            const action = node?.data?.action || {};
            const duration = action?.duration;

            if (!action?.audio) {
                Notify.notify(`Node "${label}" must select an audio file`);
                return false;
            }

            if (!duration || !Number.isInteger(Number(duration)) || Number(duration) <= 0) {
                Notify.notify(`Node "${label}" must have a valid record duration (positive integer)`);
                return false;
            }
        }

        /* Rule 6: Gather node must have menu audio */
        if (
            node.type === "gatherNode" &&
            !node?.data?.action?.menu?.audio
        ) {
            Notify.notify(`Node "${label}" must select an audio file`);
            return false;
        }
    }

    return true;
};

export const formatDate = (date) => {
    if (!date) return ""
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const formatDateTime = (cell) => {
    if (!cell) return "-";

    const date = new Date(cell.replace(" ", "T"));

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true
    });
};
